import { CliError, EXIT_INPUT } from "../../../common.js";
import {
  ACTION_TYPE_SET,
  IMPORTANCE_TIER_SET,
  NON_ACTOR_SPEAKER_KIND_SET,
  type ActionV3,
  type ActorV3,
  type EpisodeV3,
  type LocationV3,
  type PropV3,
  type SceneV3,
  type ScriptV3,
  type StateV3,
} from "./types.js";

type RecordValue = Record<string, unknown>;

function isRecord(value: unknown): value is RecordValue {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function optionalString(record: RecordValue, key: string): boolean {
  return record[key] === undefined || typeof record[key] === "string";
}

function isJsonValue(value: unknown): boolean {
  if (value === null
    || typeof value === "string"
    || typeof value === "number"
    || typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.every(isJsonValue);
  return isRecord(value) && Object.values(value).every(isJsonValue);
}

function hasValidExtension(record: RecordValue): boolean {
  return record.extend === undefined
    || (isRecord(record.extend) && Object.values(record.extend).every(isJsonValue));
}

function isState(value: unknown): value is StateV3 {
  return isRecord(value)
    && typeof value.state_id === "string"
    && typeof value.state_name === "string"
    && typeof value.description === "string"
    && hasValidExtension(value);
}

function isActor(value: unknown): value is ActorV3 {
  return isRecord(value)
    && typeof value.actor_id === "string"
    && typeof value.actor_name === "string"
    && typeof value.description === "string"
    && typeof value.importance === "string"
    && IMPORTANCE_TIER_SET.has(value.importance)
    && (value.aliases === undefined || (Array.isArray(value.aliases) && value.aliases.every((item) => typeof item === "string")))
    && (value.role_type === undefined || value.role_type === "主角" || value.role_type === "配角")
    && Array.isArray(value.states)
    && value.states.every(isState)
    && hasValidExtension(value);
}

function isLocation(value: unknown): value is LocationV3 {
  return isRecord(value)
    && typeof value.location_id === "string"
    && typeof value.location_name === "string"
    && typeof value.description === "string"
    && typeof value.importance === "string"
    && IMPORTANCE_TIER_SET.has(value.importance)
    && (value.aliases === undefined || (Array.isArray(value.aliases) && value.aliases.every((item) => typeof item === "string")))
    && Array.isArray(value.states)
    && value.states.every(isState)
    && hasValidExtension(value);
}

function isProp(value: unknown): value is PropV3 {
  return isRecord(value)
    && typeof value.prop_id === "string"
    && typeof value.prop_name === "string"
    && typeof value.description === "string"
    && typeof value.importance === "string"
    && IMPORTANCE_TIER_SET.has(value.importance)
    && (value.aliases === undefined || (Array.isArray(value.aliases) && value.aliases.every((item) => typeof item === "string")))
    && Array.isArray(value.states)
    && value.states.every(isState)
    && hasValidExtension(value);
}

function isAction(value: unknown): value is ActionV3 {
  if (!isRecord(value)
    || typeof value.type !== "string"
    || !ACTION_TYPE_SET.has(value.type)
    || typeof value.content !== "string"
    || !optionalString(value, "emotion")) return false;
  if (value.speaker !== undefined) {
    if (!isRecord(value.speaker)) return false;
    if (typeof value.speaker.actor_id === "string") {
      if (!optionalString(value.speaker, "state_id")) return false;
    } else if (typeof value.speaker.kind === "string") {
      if (!NON_ACTOR_SPEAKER_KIND_SET.has(value.speaker.kind) || !optionalString(value.speaker, "label")) return false;
    } else {
      return false;
    }
  }
  if (value.transition_prompt !== undefined) {
    if (!isRecord(value.transition_prompt)
      || typeof value.transition_prompt.target !== "string"
      || typeof value.transition_prompt.process !== "string"
      || typeof value.transition_prompt.contrast !== "string") return false;
  }
  return hasValidExtension(value);
}

function isScene(value: unknown): value is SceneV3 {
  if (!isRecord(value)
    || typeof value.scene_id !== "string"
    || !Array.isArray(value.actors)
    || !Array.isArray(value.props)
    || !Array.isArray(value.actions)
    || !value.actions.every(isAction)) return false;
  if (!value.actors.every((ref) => isRecord(ref) && typeof ref.actor_id === "string" && optionalString(ref, "state_id"))) return false;
  if (!value.props.every((ref) => isRecord(ref) && typeof ref.prop_id === "string" && optionalString(ref, "state_id"))) return false;
  if (value.location !== null
    && (!isRecord(value.location)
      || typeof value.location.location_id !== "string"
      || !optionalString(value.location, "state_id"))) return false;
  if (value.environment !== undefined
    && (!isRecord(value.environment)
      || !optionalString(value.environment, "space")
      || !optionalString(value.environment, "time"))) return false;
  return hasValidExtension(value);
}

function isEpisode(value: unknown): value is EpisodeV3 {
  return isRecord(value)
    && typeof value.episode_id === "string"
    && optionalString(value, "title")
    && optionalString(value, "synopsis")
    && Array.isArray(value.scenes)
    && value.scenes.every(isScene)
    && hasValidExtension(value);
}

export function isScriptV3(value: unknown): value is ScriptV3 {
  return isRecord(value)
    && value.version === 3
    && typeof value.title === "string"
    && typeof value.worldview === "string"
    && typeof value.style === "string"
    && optionalString(value, "worldview_raw")
    && optionalString(value, "synopsis")
    && optionalString(value, "logline")
    && optionalString(value, "theme")
    && (value.main_characters === undefined
      || (Array.isArray(value.main_characters)
        && value.main_characters.every((item) => isRecord(item) && typeof item.name === "string" && typeof item.role === "string")))
    && Array.isArray(value.actors)
    && value.actors.every(isActor)
    && Array.isArray(value.locations)
    && value.locations.every(isLocation)
    && Array.isArray(value.props)
    && value.props.every(isProp)
    && Array.isArray(value.episodes)
    && value.episodes.every(isEpisode)
    && hasValidExtension(value);
}

export function requireScriptV3(value: unknown): ScriptV3 {
  if (isScriptV3(value)) return value;
  throw new CliError("The local file is not a structurally readable Script v3 document.", {
          exitCode: EXIT_INPUT,
          errorCode: "SCRIPT_V3_SHAPE_INVALID",
          hint: "Repair the local Script v3 structure, then rerun authoring.",
          data: { expected: ["Script v3 with typed assets, episodes, scenes, and actions"], actual: [Array.isArray(value) ? "array" : typeof value] },
        });
}
